import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, User } from "lucide-react";
// 👇 Importamos solo la campanita de proveedores
import SupplierNotificationBell from '@/features/proveedores/components/SupplierNotificationBell';
import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService';
import { getSupplierProfile } from '@/features/proveedores/api/supplierProfile.service';

export const SupplierHeader = () => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [profileDisplayName, setProfileDisplayName] = useState<string | null>(
    null,
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Obtenemos los datos del proveedor del localStorage
  const supplier = supplierAuthService.getSupplierData();
  const supplierDisplayName =
    profileDisplayName ||
    supplier?.mainContactName ||
    supplier?.legalName ||
    "Proveedor";
  const supplierSecondaryLine =
    supplier?.legalName || supplier?.mainContactName || supplier?.rfc || "";

  useEffect(() => {
    let isMounted = true;

    const loadSupplierDisplayName = async () => {
      try {
        const profile = await getSupplierProfile();
        const name =
          profile?.general?.mainContactName?.trim() ||
          profile?.general?.legalName?.trim() ||
          null;

        if (isMounted) {
          setProfileDisplayName(name);
        }
      } catch {
        if (isMounted) {
          setProfileDisplayName(null);
        }
      }
    };

    loadSupplierDisplayName();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    supplierAuthService.removeToken();
    // Aseguramos borrar cualquier rastro de sesión
    localStorage.removeItem("access_token");
    localStorage.removeItem("supplier_access_token");
    localStorage.removeItem("supplier_user");

    navigate("/proveedores/login");
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-end px-6 shadow-sm z-10 shrink-0 relative">
      <div className="flex items-center gap-4">
        {/* 🔔 La campanita ya contiene la lógica de color azul y navegación */}
        <SupplierNotificationBell />

        <div className="h-6 w-px bg-gray-200 mx-1"></div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium text-gray-900">
                {supplierDisplayName}
              </span>
              <span className="text-xs text-gray-500">
                {supplierSecondaryLine}
              </span>
            </div>

            <div className="h-9 w-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-700">
              <User className="w-5 h-5" />
            </div>

            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                isMenuOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-200 shadow-xl py-2 z-50 bg-white">
              <div className="px-4 py-3 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-900">
                  {supplierDisplayName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {supplierSecondaryLine}
                </p>
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors text-left text-red-600 mt-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
